/* @jsx h */
import { getOrderDisplayName } from "@lemmonhq/orders";
import { FoodType, Order } from "@lemmonhq/types";
import { computed, defineComponent, ref, Ref, h } from "vue";
import { OrderCardDeliveryInfo } from "./OrderCardDeliveryInfo";
import { OrderCardFoodTypeStatusButton } from "./OrderCardFoodTypeStatusButton";
import { OrderCardItems } from "./OrderCardItems";
import { OrderCardOrderStatusButton } from "./OrderCardOrderStatusButton";
import { OrderCardPickupInfo } from "./OrderCardPickupInfo";
import { PendingOrderCard } from "./PendingOrderCard/index";
import { useLocalization } from "@lemmonhq/uikit";
import { useAuth } from "../../../providers/Auth";
import {
  formatOrderDate,
  getRealOrder,
  getSortedFoodTypesList,
  groupOrderItemsByFoodType,
} from "./helpers";

export interface OrderCardProps {
  order: Order;
  orders: Ref<Order[]>;
  onFoodStatusChange: (
    orderId: number,
    foodTypeId: number,
    currentStatus: string,
  ) => Promise<void>;
  onOrderCancel: (orderId: number) => void;
  closeOpenOrder: (order: Order) => Promise<boolean>;
  foodTypes: FoodType[];
  onOrderUpdate: (order: Order) => void;
  allowAdditions?: boolean;
  quantityMode?: "add" | "reduce" | null;
  onItemQuantityChange?: (
    orderId: number,
    itemId: number,
    newQuantity: number,
  ) => void;
  modifiedQuantities?: Record<number, number>;
}

export const OrderCardMain = defineComponent<OrderCardProps>({
  name: "OrderCardMain",
  props: [
    "order",
    "orders",
    "onFoodStatusChange",
    "onOrderCancel",
    "closeOpenOrder",
    "foodTypes",
    "onOrderUpdate",
    "allowAdditions",
    "quantityMode",
    "onItemQuantityChange",
    "modifiedQuantities",
  ],
  setup(props) {
    const { t } = useLocalization();
    const auth = useAuth();
    const loading = ref<false | string | number>(false);
    const closingOrder = ref(false);

    const realOrder = computed(() =>
      getRealOrder(props.order, props.orders.value),
    );

    const groups = computed(() => {
      if (!realOrder.value) return new Map();
      return groupOrderItemsByFoodType(
        props.order.items || [],
        realOrder.value.food_type_statuses,
      );
    });

    const foodTypesList = computed(() =>
      getSortedFoodTypesList(
        props.foodTypes,
        realOrder.value?.food_type_statuses,
      ),
    );

    const updateFoodStatus = async (
      foodTypeId: number,
      currentStatus: string,
    ) => {
      loading.value = foodTypeId;
      try {
        await props.onFoodStatusChange(
          realOrder.value!.id,
          foodTypeId,
          currentStatus,
        );
      } finally {
        loading.value = false;
      }
    };

    const handleQuantityChange = (itemId: number, newQuantity: number) => {
      if (props.onItemQuantityChange) {
        props.onItemQuantityChange(realOrder.value!.id, itemId, newQuantity);
      }
    };

    return () => {
      const { closeOpenOrder } = props;

      if (props.order.status === "new") {
        return (
          <div class=" max-h-full flex flex-col select-none  ">
            <PendingOrderCard
              order={props.order}
              onOrderUpdate={props.onOrderUpdate}
            />
          </div>
        );
      }

      const { date: formattedDate, time: formattedTime } = formatOrderDate(
        props.order.created_at,
      );
      return (
        <div class="px-2 py-2 flex-1 space-y-3 overflow-y-auto">
          <div class="">
            <div class="flex justify-between text-sm text-[var(--order-card-muted-content)]">
              <div class="text-left font-bold">
                {props.order.table && (
                  <div class="text-accent flex gap-1 ">
                    <span class="material-symbols-outlined text-0.5xl">
                      table_restaurant
                    </span>
                    {props.order.table.name}
                  </div>
                )}
                <div class="text-xs">
                  {auth.user.value?.restaurant.currency || "CHF"}{" "}
                  {props.order.amount?.toFixed(2)}
                </div>
              </div>
              <div class="text-right">
                <div class="text-accent font-semibold">
                  {getOrderDisplayName(props.order)}
                </div>
                <div title={formattedDate} class="text-xs">
                  {formattedTime}
                </div>
              </div>
            </div>
            {props.order.party && (
              <div class="text-right font-semibold">
                {props.order.party?.name}
              </div>
            )}
          </div>
          <div>
            <OrderCardOrderStatusButton
              order={realOrder.value!}
              onCloseOrder={closeOpenOrder}
              loading={closingOrder.value}
              onOrderUpdate={props.onOrderUpdate}
            />
          </div>
          <div class="space-y-3">
            {foodTypesList.value.map((id) => {
              const items = groups.value.get(id) || [];
              if (items.length === 0) return null;

              const type = realOrder.value!.food_type_statuses?.find(
                (el) => el.id == id,
              );
              const groupName = type ? type.name : "";

              return (
                <div
                  key={groupName || "ungrouped"}
                  class="space-y-2 border border-[var(--order-card-section-border)] px-2 py-2 rounded"
                >
                  {props.order.table_id && type ? (
                    <OrderCardFoodTypeStatusButton
                      foodTypeStatus={type}
                      onStatusChange={updateFoodStatus}
                      loading={loading.value === type.id}
                      order={props.order}
                    />
                  ) : null}
                  <OrderCardItems
                    items={items}
                    groupName={groupName}
                    allowAdditions={props.allowAdditions}
                    quantityMode={props.quantityMode}
                    onQuantityChange={handleQuantityChange}
                    modifiedQuantities={props.modifiedQuantities}
                  />
                </div>
              );
            })}
          </div>
          {props.order.notes ? (
            <div class="border border-[var(--order-card-section-border)] px-2 py-2 ">
              <div class="text-accent font-semibold">
                {t("orders.card.notes")}
              </div>
              {props.order.notes}
            </div>
          ) : null}
          {props.order.pickup && (
            <OrderCardPickupInfo pickup={props.order.pickup} />
          )}

          {props.order.delivery && (
            <OrderCardDeliveryInfo delivery={props.order.delivery} />
          )}
        </div>
      );
    };
  },
});
